import React, {KeyboardEvent, ReactElement} from "react";
import {navigate} from "gatsby";

import {css} from "/src/utils/utils";
import translate from "/src/utils/translate";
import KEY from "/src/constants/keyboard-keydown-key";

import Button from "../../buttons/button/Button";
import Icon from "../../icon/Icon";

import {getQueryStringParams} from "/src/utils/utils";

import CloseIcon from "/src/svg/close.svg";

import * as styles from "./search-input.module.scss";

interface SearchInputProps {
  hideClose?: boolean;
  onClose?: () => void;
  active: boolean;
}

const SearchInput = (props: SearchInputProps): ReactElement | null => {
  const {hideClose, onClose, active} = props;
  const [isFocusable, setIsFocusable] = React.useState<boolean>(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [mounted, setMounted] = React.useState<boolean>(false);

  const inputKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEY.ENTER) {
      search();
    }
  };

  const search = () => {
    const query = inputRef.current ? inputRef.current.value.trim() : "";

    navigate(encodeURI(query.length ? `/search?q=${query}&page=1` : "/search"));
  };

  React.useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // on mount
  React.useEffect(() => {
    const queryParam = getQueryStringParams();

    const query = queryParam?.get("q")
      ? decodeURIComponent(queryParam?.get("q")!)
      : "";

    if (query && inputRef.current && inputRef.current.value !== query) {
      inputRef.current.value = query;
    }
  });

  React.useEffect(() => {
    if (active) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }

    setIsFocusable(active);
  }, [active]);

  if (!mounted) {
    return null;
  }

  return (
    <div role="search" className={styles.searchInput__inputWrapper}>
      <input
        className={css(styles.searchInput__input)}
        placeholder={translate("Search")}
        onKeyUp={inputKeyUp}
        ref={inputRef}
        tabIndex={isFocusable ? 0 : -1}
        aria-label={translate("Search")}
        maxLength={128}
      />

      <Button
        kind="primary"
        classes={styles.searchInput__searchBtn}
        onClick={search}
        disabled={!active}
      >
        {translate("Search")}
      </Button>

      {!hideClose && (
        <button
          type="button"
          className={css(styles.searchInput__closeBtn)}
          onClick={onClose}
          tabIndex={isFocusable ? 0 : -1}
          aria-label={translate("Close search panel")}
        >
          <Icon svg={CloseIcon} classes={styles.searchInput__closeIcon} />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
